import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PlayCircle, StopCircle, MapPin, AlertTriangle, Clock } from "lucide-react";

const GEOLOCATION_TIMEOUT_MS = 12_000;
const MS_PER_MINUTE = 60_000;

export default function TimeClock() {
  const [active, setActive] = useState(null);
  const [entries, setEntries] = useState([]);
  const [busy, setBusy] = useState(false);
  const [locErr, setLocErr] = useState("");

  const load = useCallback(async () => {
    const [a, list] = await Promise.all([api.get("/time-entries/active"), api.get("/time-entries?limit=50")]);
    setActive(a.data);
    setEntries(list.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const getPos = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("Geolocation not supported by this browser"));
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ latitude: p.coords.latitude, longitude: p.coords.longitude, accuracy_m: p.coords.accuracy }),
      (err) => reject(new Error(err.message)),
      { enableHighAccuracy: true, timeout: GEOLOCATION_TIMEOUT_MS, maximumAge: 0 }
    );
  });

  const doClockIn = async () => {
    setBusy(true); setLocErr("");
    try {
      const pos = await getPos();
      await api.post("/time-entries/clock-in", pos);
      toast.success("Clocked in");
      load();
    } catch (e) {
      const msg = e?.response ? formatApiError(e) : e.message;
      setLocErr(msg); toast.error(msg);
    }
    setBusy(false);
  };

  const doClockOut = async () => {
    setBusy(true); setLocErr("");
    try {
      const pos = await getPos();
      await api.post("/time-entries/clock-out", pos);
      toast.success("Clocked out");
      load();
    } catch (e) {
      const msg = e?.response ? formatApiError(e) : e.message;
      setLocErr(msg); toast.error(msg);
    }
    setBusy(false);
  };

  const now = new Date();
  const runningMinutes = active ? Math.floor((now - new Date(active.clock_in_at)) / MS_PER_MINUTE) : 0;
  const hrs = Math.floor(runningMinutes / 60);
  const mins = runningMinutes % 60;

  return (
    <div className="p-10">
      <div className="overline mb-2">§ TIME CLOCK</div>
      <h1 className="font-heading font-black text-5xl tracking-tight mb-8">Time Tracking</h1>

      <div className="grid grid-cols-12 gap-6 mb-10">
        <div className={`col-span-12 lg:col-span-7 hard-card hard-card-shadow p-8 ${active ? "bg-good/10" : ""}`}>
          <div className="overline mb-2">{active ? "CURRENTLY CLOCKED IN" : "READY TO CLOCK IN"}</div>
          {active ? (
            <>
              <div className="font-mono font-black text-6xl mb-4 tabular-nums">{String(hrs).padStart(2, "0")}:{String(mins).padStart(2, "0")}</div>
              <div className="font-mono text-xs text-muted-foreground mb-6">
                Since {new Date(active.clock_in_at).toLocaleString()}
                {active.worksite_name && <> · <MapPin className="w-3 h-3 inline" /> {active.worksite_name}</>}
              </div>
              {active.flagged && (
                <div className="border-2 border-warn bg-warn/10 p-3 mb-6 font-mono text-xs flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <div>{active.flag_reason}</div>
                </div>
              )}
              <button data-testid="clock-out-btn" onClick={doClockOut} disabled={busy} className="hard-btn w-full py-4 font-mono uppercase text-sm tracking-widest inline-flex items-center justify-center gap-3 disabled:opacity-40" style={{ background: "#E1301A", borderColor: "#E1301A", boxShadow: "4px 4px 0 0 #0A0A0A" }}>
                <StopCircle className="w-5 h-5" /> {busy ? "Getting location…" : "Clock Out"}
              </button>
            </>
          ) : (
            <>
              <div className="font-mono font-black text-6xl mb-4 tabular-nums text-muted-foreground">00:00</div>
              <div className="font-mono text-xs text-muted-foreground mb-6">Your device will share GPS coordinates when you clock in.</div>
              <button data-testid="clock-in-btn" onClick={doClockIn} disabled={busy} className="hard-btn w-full py-4 font-mono uppercase text-sm tracking-widest inline-flex items-center justify-center gap-3 disabled:opacity-40" style={{ background: "#00A050", borderColor: "#00A050", boxShadow: "4px 4px 0 0 #0A0A0A" }}>
                <PlayCircle className="w-5 h-5" /> {busy ? "Getting location…" : "Clock In"}
              </button>
            </>
          )}
          {locErr && <div className="mt-4 border-2 border-bad text-bad font-mono text-xs p-3">{locErr}</div>}
        </div>
        <div className="col-span-12 lg:col-span-5 hard-card p-6">
          <div className="overline mb-3">HOW IT WORKS</div>
          <ol className="font-mono text-sm space-y-3 leading-relaxed">
            <li><b>1.</b> When you tap Clock In / Out, your browser asks for location permission.</li>
            <li><b>2.</b> We compute your distance from your assigned worksite using the Haversine formula.</li>
            <li><b>3.</b> If you're outside the allowed radius, the entry is <b>flagged</b> for admin review.</li>
            <li><b>4.</b> Duration is calculated to the second and rolled into your next payroll run.</li>
          </ol>
        </div>
      </div>

      <div className="mb-3">
        <div className="overline mb-1">§ RECENT ENTRIES</div>
        <h2 className="font-heading font-black text-3xl tracking-tight">History</h2>
      </div>
      <div className="hard-card overflow-x-auto">
        <table className="w-full font-mono text-sm">
          <thead className="bg-ink text-white">
            <tr>
              <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">Date</th>
              <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">Worksite</th>
              <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">Clock In</th>
              <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">Clock Out</th>
              <th className="text-right px-3 py-3 uppercase text-xs tracking-widest">Duration</th>
              <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">Status</th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-muted-foreground">No entries yet.</td></tr>}
            {entries.map((e, i) => (
              <tr key={e.id} className={i % 2 === 0 ? "bg-white" : "bg-[#F9F9F9]"} data-testid={`time-row-${e.id}`}>
                <td className="px-3 py-3 border-t border-ink/20">{e.clock_in_at?.slice(0, 10)}</td>
                <td className="px-3 py-3 border-t border-ink/20">{e.worksite_name || "—"}</td>
                <td className="px-3 py-3 border-t border-ink/20">{e.clock_in_at ? new Date(e.clock_in_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                <td className="px-3 py-3 border-t border-ink/20">{e.clock_out_at ? new Date(e.clock_out_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : <span className="text-good">Active</span>}</td>
                <td className="px-3 py-3 border-t border-ink/20 text-right font-black">{e.duration_hours?.toFixed(2) || "—"}h</td>
                <td className="px-3 py-3 border-t border-ink/20">
                  {e.flagged ? <span className="px-2 py-0.5 bg-bad text-white text-[10px] uppercase tracking-widest">Flagged</span> : <span className="px-2 py-0.5 bg-good text-white text-[10px] uppercase tracking-widest">OK</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
